const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 2) => `${Number(n ?? 0).toFixed(digits)}%`;

export const buildTriGaugeStyle = (tri = 0) => {
  const pct = Math.min(100, Math.max(0, (Number(tri) / 10) * 100));
  return {
    background: `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(#ef4444 0 ${pct}%, rgba(239,68,68,.13) ${pct}% 100%)`,
  };
};

export const buildDeploymentHeroHeadline = () => "Deployment Risk Command Center";

export const buildDeploymentHeroNarrative = (blockData = []) => {
  const top = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))
    .slice(0, 3)
    .map((b) => b.block_name);
  const names = top.length >= 3 ? `${top[0]}, ${top[1]} and ${top[2]}` : top.join(", ");
  return `Risk is concentrated in a small set of blocks where deputation, cross-school teaching and high teacher-per-school variance overlap.${names ? ` Focus first on ${names}.` : ""}`;
};

export const buildDeploymentStatusChips = (overview = {}) => {
  const tri = overview.teacher_risk_index ?? 0;
  const riskLabel = tri >= 7 ? "High risk" : tri >= 4 ? "Moderate Risk" : "Low risk";
  return [
    { label: riskLabel, className: "" },
    { label: `${fmt(overview.deputation_cy)} on Deputation`, className: "orange" },
    { label: `${fmt(overview.other_school_cy)} Cross-School Teachers`, className: "blue" },
  ];
};

export const buildDeploymentPrimaryAction = (blockData = []) => {
  const top = [...blockData]
    .sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))
    .slice(0, 3)
    .map((b) => b.block_name)
    .join(", ");
  if (top) {
    return `Freeze new deputation approvals in high-risk blocks (${top}) until block-level staffing gap review is completed.`;
  }
  return "Freeze new deputation approvals in high-risk blocks until block-level staffing gap review is completed.";
};

export const buildDeploymentKpis = (overview = {}) => [
  {
    label: "On Deputation",
    value: fmt(overview.deputation_cy),
    sub: `${fmtPct(overview.deputation_ratio)} of teachers`,
    cardClass: "orange",
  },
  {
    label: "Cross-School Teaching",
    value: fmt(overview.other_school_cy),
    sub: `${fmtPct(overview.cross_school_ratio)} of teachers`,
    cardClass: "purple",
  },
  {
    label: "Teacher Risk Index",
    value: String(overview.teacher_risk_index ?? 0),
    sub: "TRI composite score",
    cardClass: "red",
  },
  {
    label: "Total Schools",
    value: fmt(overview.total_schools),
    sub: "in selected dataset",
    cardClass: "blue",
  },
];

export const buildPressureSnapshotCards = (overview = {}, blockData = []) => {
  const highRiskCount = blockData.filter((b) => (b.risk_index ?? 0) >= 5).length;
  const topRisk = [...blockData].sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))[0];

  return [
    {
      id: "deputation",
      title: "Deputation Load",
      score: fmtPct(overview.deputation_ratio),
      scoreLabel: "of teachers",
      width: Math.min(100, (overview.deputation_ratio ?? 0) * 20),
      barColor: "var(--tdp-secondary)",
      iconClass: "orange",
      dotClass: "amber",
      metaLeft: `${fmt(overview.deputation_cy)} teachers`,
      metaRight: (overview.deputation_ratio ?? 0) > 2 ? "Elevated" : "Watch level",
    },
    {
      id: "cross-school",
      title: "Cross-School Dependency",
      score: fmtPct(overview.cross_school_ratio),
      scoreLabel: "of teachers",
      width: Math.min(100, (overview.cross_school_ratio ?? 0) * 20),
      barColor: "var(--tdp-purple)",
      iconClass: "purple",
      dotClass: "purple",
      metaLeft: `${fmt(overview.other_school_cy)} teachers`,
      metaRight: "Coverage risk",
    },
    {
      id: "high-risk",
      title: "High-Risk Blocks",
      score: String(highRiskCount),
      scoreLabel: "blocks",
      width: Math.min(100, highRiskCount * 6),
      barColor: "var(--tdp-danger)",
      iconClass: "red",
      dotClass: "red",
      metaLeft: `Top risk ${topRisk?.risk_index ?? 0}`,
      metaRight: topRisk?.block_name ?? "—",
    },
    {
      id: "staffing",
      title: "Staffing Base",
      score: fmt(overview.teachers_cy),
      scoreLabel: "teachers",
      width: 82,
      barColor: "var(--tdp-accent)",
      iconClass: "green",
      dotClass: "",
      metaLeft: `${fmt(overview.total_schools)} schools`,
      metaRight: `${overview.avg_teachers_per_school ?? 0} avg/school`,
    },
  ];
};

export const buildDeputationDualRows = (deploymentRisk = []) => {
  const rows = [...deploymentRisk].slice(0, 10);
  const maxDep = Math.max(...rows.map((r) => r.deputation_cy ?? 0), 1);
  const maxOther = Math.max(...rows.map((r) => r.other_school_cy ?? 0), 1);
  return rows.map((row) => ({
    block: row.block_name,
    deputationWidth: Math.round(((row.deputation_cy ?? 0) / maxDep) * 100),
    otherWidth: Math.round(((row.other_school_cy ?? 0) / maxOther) * 100),
  }));
};

const triBarClass = (tri) => {
  if (tri >= 7) return "red";
  if (tri >= 5.5) return "amber";
  return "";
};

export const buildTriRiskRows = (blockData = []) => {
  const rows = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))
    .slice(0, 10);
  const maxTri = Math.max(...rows.map((r) => r.risk_index ?? 0), 1);
  return rows.map((row) => ({
    block: row.block_name,
    tri: row.risk_index ?? 0,
    display: (row.risk_index ?? 0).toFixed(1),
    width: Math.round(((row.risk_index ?? 0) / maxTri) * 100),
    barClass: triBarClass(row.risk_index ?? 0),
  }));
};

export const buildCapacityChart = (blockData = []) => {
  const rows = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.teachers_cy ?? 0) - (a.teachers_cy ?? 0))
    .slice(0, 15);
  const maxTeachers = Math.max(...rows.map((r) => r.teachers_cy ?? 0), 1);
  const avgs = rows.map((r) => r.avg_teachers_per_school ?? 0);
  const maxAvg = Math.max(...avgs, 1);
  const minAvg = Math.min(...avgs);
  const chartHeight = 245;
  const lineHeight = 205;
  const width = 980;
  const step = rows.length > 1 ? width / (rows.length - 1) : 0;

  const columns = rows.map((row, i) => {
    const avg = row.avg_teachers_per_school ?? 0;
    const norm = maxAvg === minAvg ? 0.5 : (avg - minAvg) / (maxAvg - minAvg);
    return {
      block: row.block_name,
      barHeight: Math.max(24, Math.round(((row.teachers_cy ?? 0) / maxTeachers) * chartHeight)),
      lineX: Math.round(i * step),
      lineY: Math.round(12 + (1 - norm) * (lineHeight - 24)),
    };
  });

  const polyline = columns.map((c) => `${c.lineX},${c.lineY}`).join(" ");
  const circles = columns.map((c) => ({ cx: c.lineX, cy: c.lineY }));

  return { columns, polyline, circles };
};

const primaryDriver = (block) => {
  const below = block.below_grad_pct ?? 0;
  const dep = block.deputation_pct ?? 0;
  const cross = block.cross_school_pct ?? 0;
  const avg = block.avg_teachers_per_school ?? 0;

  if (below >= 8 && cross >= 2) return "Qualification + shared teaching";
  if (dep >= 2) return "Deputation pressure";
  if (avg < 9) return "Teacher imbalance";
  if (avg < 10) return "Capacity gap";
  if (cross >= 2) return "Shared teaching load";
  return "Small-school load";
};

const suggestedOwner = (driver) => {
  if (driver.includes("Qualification")) return "BEO + HR Cell";
  if (driver.includes("Deputation")) return "BEO";
  if (driver.includes("imbalance")) return "Cluster Officer";
  if (driver.includes("Capacity")) return "HR Cell";
  return "Block Team";
};

const watchStatus = (tri) => {
  if (tri >= 7) return { label: "Critical", className: "red" };
  if (tri >= 5.5) return { label: "High", className: "" };
  return { label: "Watch", className: "blue" };
};

export const buildRiskWatchlist = (blockData = []) => {
  return [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))
    .slice(0, 5)
    .map((block, index) => {
      const driver = primaryDriver(block);
      const status = watchStatus(block.risk_index ?? 0);
      return {
        rank: index + 1,
        block: block.block_name,
        tri: (block.risk_index ?? 0).toFixed(1),
        driver,
        owner: suggestedOwner(driver),
        status: status.label,
        statusClass: status.className,
      };
    });
};

export const buildDeploymentActionQueue = (overview = {}, blockData = [], deploymentRisk = []) => {
  const tri = overview.teacher_risk_index ?? 0;
  const topTriBlocks = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))
    .slice(0, 3)
    .map((b) => b.block_name);
  const topDeputation = [...deploymentRisk]
    .sort((a, b) => (b.deputation_pct ?? 0) - (a.deputation_pct ?? 0))
    .slice(0, 2)
    .map((b) => b.block_name)
    .filter(Boolean);

  return [
    {
      title: topTriBlocks.length ? `Review top TRI blocks: ${topTriBlocks.join(", ")}` : "Review highest TRI blocks",
      body: `District TRI is ${tri}. Validate subject shortages, vacancy clusters and deputation overuse in the highest-risk blocks.`,
      iconClass: "red",
    },
    {
      title: topDeputation.length ? `Normalize deputation in ${topDeputation.join(", ")}` : "Normalize deputation movement",
      body: topDeputation.length
        ? `These blocks exceed the district deputation pressure profile and need approval thresholds.`
        : "Introduce approval thresholds for blocks above the district median deputation load.",
      iconClass: "orange",
    },
    {
      title: "Create Shared-Teacher Roster",
      body: `Cover ${overview.total_schools ?? 0} schools with a controlled cross-school roster where chronic staffing gaps persist.`,
      iconClass: "purple",
    },
  ];
};

export const buildDeploymentReviewNote = (overview = {}) => {
  const tri = overview.teacher_risk_index ?? 0;
  return tri >= 4
    ? `Run a weekly deployment-risk review while TRI remains ${tri} (target below 4.0).`
    : `TRI is ${tri}. Maintain weekly monitoring to keep cross-school dependency stable.`;
};

export const buildTeacherDeploymentMarkdown = (overview = {}, blockData = [], deploymentRisk = []) => {
  const actions = buildDeploymentActionQueue(overview, blockData, deploymentRisk);
  const watchlist = buildRiskWatchlist(blockData);
  return [
    "## Insights",
    `- ${buildDeploymentHeroHeadline()}`,
    `- ${buildDeploymentHeroNarrative(blockData)}`,
    ...watchlist.slice(0, 3).map((w) => `- ${w.block}: ${w.driver}`),
    "",
    "## Recommendations",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${buildDeploymentPrimaryAction(blockData)}`,
    `- ${buildDeploymentReviewNote(overview)}`,
  ].join("\n");
};
